import {
  DateTimePicker,
  ProductTypeEnum,
  getCurrencyDrawerOptions,
  type Currency,
  type InputsAndDropdownsDrawerOption,
  type InputsAndDropdownsDrawerOptions,
  type Security,
} from '@talos/kyoko';
import { get } from 'lodash-es';
import { CopyFromListedSymbol, type CreateCustomSecurityForm } from './customSecurityWorkflowTypes';
import {
  getCopyFromListedSymbolOptions,
  getFormWithCopiedSecurityFields,
  getMarketOptions,
  getOptionTypeOptions,
  getProductTypeOptions,
  getSettleValueTypeOptions,
  isContractSizeGreaterThanOne,
  isCopyableSourceSecurity,
  productTypeRequiresExpiry,
  validateDisplaySymbol,
  validatePositiveInteger,
  validateSymbol,
} from './customSecurityWorkflowUtils';

function isCopiedFromListedSymbol(form: CreateCustomSecurityForm) {
  return form.CopyFromListedSymbol === CopyFromListedSymbol.Yes;
}

export function getSetupDrawerOptions({
  securitiesList,
  securitiesBySymbol,
}: {
  securitiesList: Security[];
  securitiesBySymbol: Map<string, Security>;
}): InputsAndDropdownsDrawerOption<CreateCustomSecurityForm>[] {
  return [
    {
      field: 'ProductType',
      type: 'dropdown',
      title: 'Product Type',
      getDropdownOptions: () => getProductTypeOptions(),
      getIsRequired: () => true,
      showClear: false,
      placeholder: 'Select product type',
      onChange: (_value, onFormUpdate, _form) => {
        onFormUpdate('CopySourceSymbol', undefined, true);
      },
    },
    {
      field: 'CopyFromListedSymbol',
      type: 'toggle',
      title: 'Copy from listed symbol',
      getToggleOptions: () => getCopyFromListedSymbolOptions(),
      defaultValue: CopyFromListedSymbol.Yes,
      getIsRequired: () => true,
      onChange: (_value, onFormUpdate, _form) => onFormUpdate('CopySourceSymbol', undefined, true),
    },
    {
      field: 'CopySourceSymbol',
      type: 'symbolSelector',
      title: 'Listed Symbol',
      placeholder: 'Select listed symbol',
      showFavorites: false,
      showGroups: false,
      getAvailableSecurities: form =>
        securitiesList.filter(
          security => security.ProductType === form.ProductType && isCopyableSourceSecurity(security)
        ),
      getIsHidden: form => form.CopyFromListedSymbol !== CopyFromListedSymbol.Yes,
      getIsRequired: form => form.CopyFromListedSymbol === CopyFromListedSymbol.Yes,
      getIsDisabled: form => !form.ProductType,
      guideText: 'Only Regular settle-type securities whose base currency differs from settlement currency are shown.',
      onChange: (symbol, onFormUpdate, form) => {
        if (!symbol || form == null) {
          return;
        }
        const selectedSecurity = securitiesBySymbol.get(String(symbol));
        if (!selectedSecurity) {
          return;
        }
        const updatedForm = getFormWithCopiedSecurityFields(form, selectedSecurity);
        for (const [field, value] of Object.entries(updatedForm)) {
          onFormUpdate(field, value, true);
        }
      },
    },
  ];
}

export function getInstrumentDetailsDrawerOptions({
  currenciesList,
  securitiesBySymbol,
}: {
  currenciesList: Currency[];
  securitiesBySymbol: Map<string, Security>;
}): InputsAndDropdownsDrawerOptions<CreateCustomSecurityForm> {
  const currencyOptions = getCurrencyDrawerOptions(currenciesList);
  const securitiesByDisplaySymbol = new Map(
    [...securitiesBySymbol.values()].flatMap(security =>
      security.DisplaySymbol ? [[security.DisplaySymbol, security] as const] : []
    )
  );
  return [
    {
      field: 'Symbol',
      type: 'input',
      getIsRequired: () => true,
      validate: form => validateSymbol(form, securitiesBySymbol),
    },
    {
      field: 'DisplaySymbol',
      type: 'input',
      title: 'Display Symbol',
      getIsRequired: () => true,
      validate: form => validateDisplaySymbol(form, securitiesBySymbol, securitiesByDisplaySymbol),
    },
    {
      field: 'ProductType',
      type: 'dropdown',
      title: 'Product Type',
      getDropdownOptions: () => getProductTypeOptions(),
      getIsRequired: () => true,
      getIsDisabled: () => true,
      showClear: false,
    },
    {
      field: 'Description',
      type: 'input',
    },
    [
      {
        field: 'BaseCurrency',
        type: 'dropdown',
        title: 'Base Currency',
        getDropdownOptions: () => currencyOptions,
        getIsRequired: form => form.ProductType === ProductTypeEnum.Option,
        getIsDisabled: isCopiedFromListedSymbol,
      },
      {
        field: 'QuoteCurrency',
        type: 'dropdown',
        title: 'Quote Currency',
        getDropdownOptions: () => currencyOptions,
        getIsRequired: form => form.ProductType === ProductTypeEnum.Option,
        getIsDisabled: isCopiedFromListedSymbol,
      },
    ],
    [
      {
        field: 'PositionCurrency',
        type: 'dropdown',
        title: 'Position Currency',
        getDropdownOptions: () => currencyOptions,
        // Backend constraint: PositionCurrency must be empty when ContractSize > 1.
        getIsDisabled: form => isCopiedFromListedSymbol(form) || isContractSizeGreaterThanOne(form.ContractSize),
        validate: form =>
          isContractSizeGreaterThanOne(form.ContractSize) && form.PositionCurrency
            ? 'Position Currency must be empty when Contract Size is greater than 1'
            : undefined,
      },
      {
        field: 'SettlementCurrency',
        type: 'dropdown',
        title: 'Settlement Currency',
        getDropdownOptions: () => currencyOptions,
        getIsDisabled: isCopiedFromListedSymbol,
      },
    ],
    {
      field: 'SettleValueType',
      type: 'dropdown',
      title: 'Settle Type',
      getDropdownOptions: () => getSettleValueTypeOptions(),
      showClear: false,
      // Custom instruments only support Regular, so the field is always locked.
      // It is still shown so the value is visible and sent to the API.
      getIsDisabled: () => true,
      guideText: 'Custom instruments only support Regular settlement.',
    },
    { type: 'divider' },
    {
      field: 'ContractSize',
      type: 'input',
      title: 'Contract Size',
      getIsDisabled: isCopiedFromListedSymbol,
      onChange: (value, onFormUpdate) => {
        if (isContractSizeGreaterThanOne(value)) {
          onFormUpdate('PositionCurrency', undefined, true);
        }
      },
    },
    [
      {
        field: 'MinSizeIncrement',
        type: 'input',
        title: 'Min Size Increment',
      },
      {
        field: 'DefaultSizeIncrement',
        type: 'input',
        title: 'Default Size Increment',
      },
    ],
    [
      {
        field: 'MinPriceIncrement',
        type: 'input',
        title: 'Min Price Increment',
      },
      {
        field: 'DefaultPriceIncrement',
        type: 'input',
        title: 'Default Price Increment',
        getIsRequired: () => true,
      },
    ],
    { type: 'divider' },
    [
      {
        field: 'PriceDisplaySpec',
        type: 'input',
        title: 'Price Display Spec',
        guideText:
          'Price and size display specs control formatted precision. Use M for primary digits and m for muted digits, for example M.MMm.',
      },
      {
        field: 'SizeDisplaySpec',
        type: 'input',
        title: 'Size Display Spec',
      },
    ],
    {
      type: 'divider',
      title: 'Options Details',
      getIsHidden: form => form.ProductType !== ProductTypeEnum.Option,
    },
    [
      {
        field: 'Expiry',
        type: 'custom',
        title: 'Expiry',
        getIsHidden: form => !productTypeRequiresExpiry(form.ProductType),
        getIsRequired: form => productTypeRequiresExpiry(form.ProductType),
        getIsDisabled: isCopiedFromListedSymbol,
        renderer: ({ form, onFormUpdate, drawerOption }) => {
          const value = get(form, drawerOption.field);
          return (
            <DateTimePicker
              value={value instanceof Date ? value : null}
              onChange={expiry => onFormUpdate(drawerOption.field, expiry)}
              showShortcuts={false}
              disabled={drawerOption.getIsDisabled?.(form)}
              data-testid="entity-admin-drawer-expiry"
            />
          );
        },
      },
      {
        field: 'StrikePrice',
        type: 'inputNumeric',
        title: 'Strike Price',
        getIsHidden: form => form.ProductType !== ProductTypeEnum.Option,
        getIsRequired: form => form.ProductType === ProductTypeEnum.Option,
        getIsDisabled: isCopiedFromListedSymbol,
        numberInputProps: { integerOnly: true },
        validate: form =>
          form.ProductType === ProductTypeEnum.Option ? validatePositiveInteger(form.StrikePrice) : undefined,
      },
    ],
    {
      field: 'OptionType',
      type: 'dropdown',
      title: 'Option Type',
      getDropdownOptions: () => getOptionTypeOptions(),
      getIsHidden: form => form.ProductType !== ProductTypeEnum.Option,
      getIsRequired: form => form.ProductType === ProductTypeEnum.Option,
      getIsDisabled: isCopiedFromListedSymbol,
      showClear: false,
    },
    { type: 'divider' },
    {
      field: 'Market',
      type: 'dropdown',
      getDropdownOptions: () => getMarketOptions(),
      showClear: true,
    },
  ];
}
